import React, { useMemo } from "react";
import cn from "classnames";
import { SelectedIcon } from "components/UI/Icons";
import { IntrinsicElements } from "types/intrinsicElements.types";
import { OnOptionClick } from "../../../../types/select.types";
import { matchesOption } from "../../../../utils/select.utils";
import { useOpenState, useSelectConfig, useSelection } from "../context/SelectContext";
import styles from "../Select.module.scss";

type SelectItemProps = Omit<IntrinsicElements["div"], "onClick"> & {
  name: string;
  value: string;
  onClick?: OnOptionClick;
  children: React.ReactNode;
};

export const SelectItem = ({ children, className, name, value, onClick, ...props }: SelectItemProps) => {
  const [selection, updateSelection] = useSelection();
  const [, setIsOpen] = useOpenState();
  const { closeAfterSelection, withCheckbox } = useSelectConfig();

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    updateSelection({ name, value });
    onClick?.(e, { name, value });
    if (closeAfterSelection) setIsOpen(false);
  };

  const isChecked = useMemo(() => selection.some(matchesOption({ name, value })), [name, value, selection]);

  return (
    <div className={cn(className, styles.item, { [styles.itemChecked]: isChecked })} onClick={handleClick} {...props}>
      {withCheckbox && (
        <span className={cn(styles.itemCheckbox, { [styles.itemCheckboxChecked]: isChecked })} aria-hidden="true">
          {isChecked && <SelectedIcon className={styles.itemCheckboxIcon} />}
        </span>
      )}
      {children}
    </div>
  );
};
